/**
 * Helpers voor het lezen/schrijven van de deterministische kleur/dim-overlay-
 * regelset op een diagram.
 *
 * Net als de heat-map- en roadmap-matrix-config wordt de regelset gestasht
 * onder een gereserveerde `Diagram.viewState`-key (`OVERLAY_RULES_KEY`) in de
 * bestaande `view_state_json`-blob — geen aparte D1-kolom, geen migratie. De
 * renderer negeert deze entry omdat de key geen concept/relation-id is.
 */
import { OVERLAY_RULES_KEY, type OverlayRuleSet, type ViewState } from "@ae/domain";

export function readOverlayRules(viewState: ViewState | undefined | null): OverlayRuleSet | null {
  const entry = viewState?.[OVERLAY_RULES_KEY];
  const blob = entry?.style as Record<string, unknown> | undefined;
  if (!blob) return null;
  const rules = Array.isArray(blob.rules) ? (blob.rules as OverlayRuleSet["rules"]) : [];
  return {
    enabled: blob.enabled === true,
    strategy: blob.strategy === "last-match" ? "last-match" : "first-match",
    rules,
  };
}

/** Bouwt een nieuwe viewState met de regelset bijgewerkt. Muteert niet. */
export function writeOverlayRules(viewState: ViewState, ruleset: OverlayRuleSet): ViewState {
  const blob: Record<string, unknown> = {
    enabled: ruleset.enabled,
    strategy: ruleset.strategy ?? "first-match",
    rules: ruleset.rules,
  };
  return {
    ...viewState,
    // Sentinel-entry: type+ref vereist sinds Fase 2; key zelf als ref-value.
    [OVERLAY_RULES_KEY]: { type: "object", ref: OVERLAY_RULES_KEY, style: blob },
  };
}

export function hasOverlayRules(ruleset: OverlayRuleSet | null | undefined): boolean {
  return Boolean(ruleset && ruleset.rules.length > 0);
}

/** True wanneer de overlay actief kleurt (aan én minstens één actieve regel). */
export function isOverlayActive(ruleset: OverlayRuleSet | null | undefined): boolean {
  return Boolean(ruleset?.enabled && ruleset.rules.some((r) => r.enabled !== false));
}
